import type {ReactElement} from 'react';
import {useEffect, useRef} from 'react';
import {useAppSelector} from '@/hooks';
import {Text, TextSize} from '@Components/text';
import type {CheckoutStepProps} from '@Components/checkout-flow/checkout-flow.types';
import {CheckoutMode} from '@Components/checkout-flow/checkout-flow.types';
import {getConfirmationTitle} from '@Components/checkout-flow/checkout-flow.library';
import {trackPremiumPurchase} from '@Components/checkout-flow/checkout-flow-tracking.library';
import {ConfirmationCarousel} from '@Components/checkout-flow/components/confirmation-carousel/confirmation-carousel';
import {InviteTeamBanner} from '@Components/checkout-flow/components/invite-team-banner/invite-team-banner';
import styles from './premium-confirmation-step.module.scss';

export function PremiumConfirmationStep({request, controller}: CheckoutStepProps): ReactElement {
  const confirmation = useAppSelector((state) => state.checkoutFlow.confirmation);
  const teamName = useAppSelector((state) => state.checkoutFlow.dialogData?.teamName ?? '');
  const hasTrackedRef = useRef(false);
  const isTrial = request.mode === CheckoutMode.SUBSCRIPTION && request.isTrial;
  const isWelcomeToTeam = request.mode === CheckoutMode.WELCOME_TO_TEAM;
  const subtitle = isTrial ? window.i18next.t('pmwjs_enjoy_all_premium_features_free_for_14_days') : window.i18next.t('pmwjs_say_hello_to_faster_design_and_smarter_marketing');

  useEffect(() => {
    const trackingData = confirmation?.trackingData ?? null;
    if (hasTrackedRef.current || trackingData === null) {
      return;
    }
    hasTrackedRef.current = true;
    trackPremiumPurchase({ga4PurchaseEventParams: confirmation?.ga4PurchaseEventParams ?? null, trackingData, isTrial});
  }, [confirmation]);

  const getInviteTeamBanner = (): ReactElement | null => {
    if (isTrial) {
      return null;
    }
    return <InviteTeamBanner teamName={isWelcomeToTeam ? teamName : ''} onBeforeOpenInvite={controller.dismiss} />;
  };

  return (
    <div className={styles.container}>
      <div className={styles.titleContainer}>
        <Text val={getConfirmationTitle(request)} size={TextSize.LG} className={styles.title} />
        <Text val={subtitle} size={TextSize.MD} className="content-body" />
      </div>
      <ConfirmationCarousel />
      {getInviteTeamBanner()}
    </div>
  );
}
